import { z } from 'zod';
import { Kuerzel, Passwort } from './auth';
import { BrancheSchema } from './branchen';
import type { Konditionen } from './abrechnung';
import type { DxlZustimmungDokument } from './plattformRecht';

/* ------------------------------------------------------------------ Selbst-Registrierung eines Marktplatzes */

/** Testphase nach der Registrierung (ohne Grundgebühr) */
export const TESTPHASE_TAGE = 14;
/** Gültigkeit des Bestätigungslinks */
export const REGISTRIERUNG_GUELTIG_STUNDEN = 48;

/** Kürzel, die nicht vergeben werden – Adressen und Begriffe der Plattform */
export const RESERVIERTE_KUERZEL = ['DXL', 'API', 'APP', 'ADMIN', 'ROOT', 'PORTAL', 'SHOP', 'TEST', 'DEMO', 'HILFE', 'SUPPORT', 'MAIL', 'WWW', 'M'] as const;
export const kuerzelReserviert = (k: string) => (RESERVIERTE_KUERZEL as readonly string[]).includes(k.toUpperCase());

const UMLAUTE: Record<string, string> = { Ä: 'AE', Ö: 'OE', Ü: 'UE', ß: 'SS' };
const FUELLWOERTER = new Set(['GMBH', 'AG', 'UG', 'KG', 'OHG', 'GBR', 'EK', 'MBH', 'CO', 'UND', 'DER', 'DIE', 'DAS', 'THE', 'OF', 'DE']);

/** Wörter eines Namens in Großbuchstaben, ohne Umlaute, Sonderzeichen und Rechtsformen */
function woerter(name: string): string[] {
  return name
    .toUpperCase()
    .replace(/[ÄÖÜß]/g, (z) => UMLAUTE[z] ?? z)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^A-Z0-9]+/)
    .filter((w) => w && !FUELLWOERTER.has(w));
}

/**
 * Vorschlag für das Kürzel aus dem Namen des Marktplatzes:
 * ein Wort → die ersten vier Zeichen („Uhrenwelt“ → UHRE), mehrere → Anfangsbuchstaben („Nordwind Markt“ → NM, ergänzt auf drei: NMA).
 */
export function kuerzelVorschlag(name: string): string {
  const w = woerter(name);
  if (!w.length) return '';
  if (w.length === 1) return w[0]!.slice(0, 4);
  let k = w.map((x) => x[0]).join('').slice(0, 4);
  if (k.length < 3) k = (k + w.at(-1)!.slice(1)).slice(0, 3);
  return k;
}

/** Weitere Vorschläge, wenn das gewünschte Kürzel vergeben ist */
export function kuerzelAlternativen(name: string, gewuenscht: string): string[] {
  const basis = (gewuenscht || kuerzelVorschlag(name)).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 5);
  const w = woerter(name);
  const kandidaten = new Set<string>();
  if (w[0]) {
    kandidaten.add(w[0].slice(0, 3));
    kandidaten.add(w[0].slice(0, 5));
    if (w[1]) kandidaten.add((w[0].slice(0, 2) + w[1].slice(0, 2)));
  }
  if (basis.length >= 1) for (let i = 1; i <= 9; i++) kandidaten.add(`${basis.slice(0, 5)}${i}`);
  return [...kandidaten].filter((k) => /^[A-Z0-9]{2,6}$/.test(k) && k !== gewuenscht && !kuerzelReserviert(k));
}

const Website = z
  .string()
  .trim()
  .max(190)
  .refine((v) => !v || /^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(\/\S*)?$/i.test(v), 'Bitte eine gültige Adresse angeben, z. B. www.beispiel.de')
  .default('');

/**
 * Was registriert wird: ein Marktplatz (Modul marktplatz) oder ein Shop nur mit Retail Media (RM-8, Modul werbung).
 * Ein Shop hat keine Branche – sie steuert nur Vorlagen und Kategorien des Marktplatzes.
 */
export const REGISTRIERUNG_PRODUKTE = { marktplatz: 'Marktplatz', werbung: 'Shop mit Retail Media' } as const;
export type RegistrierungProdukt = keyof typeof REGISTRIERUNG_PRODUKTE;
export const RegistrierungProduktSchema = z.enum(['marktplatz', 'werbung']);

export const MarktplatzRegistrierungSchema = z.object({
  produkt: RegistrierungProduktSchema.default('marktplatz'),
  firma: z.string().trim().min(2, 'Bitte den Namen des Marktplatzes bzw. der Firma angeben').max(120),
  kuerzel: Kuerzel,
  branche: BrancheSchema.default('allgemein'),
  name: z.string().trim().min(2, 'Bitte den Namen des Ansprechpartners angeben').max(120),
  email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190),
  passwort: Passwort,
  website: Website,
  /** Zustimmung zu AGB/Plattformvertrag und Kenntnisnahme der Datenschutzerklärung von DXL */
  zustimmung: z.literal(true, { errorMap: () => ({ message: 'Bitte stimmen Sie den AGB zu und bestätigen Sie die Datenschutzerklärung.' }) }),
  /** Formular-Token (Zeitpunkt + Signatur) gegen Bots */
  formular: z.string().max(100).default(''),
  /** Lockfeld – bleibt bei Menschen leer */
  telefax: z.string().max(200).optional(),
  /** Kopplungstoken aus der App „DXL Retail Media“ (E2): nach dem Double-Opt-in wird der Shop automatisch verknüpft */
  app_kopplung: z.string().trim().max(200).optional(),
});
export type MarktplatzRegistrierungEingabe = z.input<typeof MarktplatzRegistrierungSchema>;

/** Rechtstext von DXL, auf den die Registrierung verweist (nur veröffentlichte Fassungen) */
export interface RegistrierungRechtstext {
  dokument: 'dxl_agb' | 'dxl_avv' | 'dxl_datenschutz';
  titel: string;
  version: string;
  url: string;
}

export interface RegistrierungInfo {
  formular: string;
  mindestSekunden: number;
  testphaseTage: number;
  rechtstexte: RegistrierungRechtstext[];
}

export const KuerzelPruefenSchema = z.object({
  kuerzel: z.string().trim().toUpperCase().max(10),
  name: z.string().trim().max(120).default(''),
});

export interface KuerzelPruefung {
  kuerzel: string;
  frei: boolean;
  grund: 'ungueltig' | 'reserviert' | 'vergeben' | null;
  vorschlaege: string[];
}

export const RegistrierungBestaetigenSchema = z.object({ token: z.string().regex(/^[A-Za-z0-9_-]{43}$/) });

export interface RegistrierungBestaetigt {
  kuerzel: string;
  /** nur bei einem Shop mit Retail Media (werbung) – die Antwort für Marktplätze bleibt wie bisher */
  produkt?: 'werbung';
  name: string;
  /** Kürzel war bei der Bestätigung inzwischen vergeben – ein freies wurde gewählt */
  kuerzelGeaendert: boolean;
  /** Registrierung aus der App „DXL Retail Media“: dieser Shop ist jetzt verknüpft (myshopify-Domain) */
  shop?: string;
}

/** In der Registrierung gespeicherte Zustimmung je Dokument */
export interface RegistrierungZustimmung {
  dokument: DxlZustimmungDokument | 'dxl_datenschutz';
  version: string;
  sha256: string;
}

/* ------------------------------------------------------------------ Paket buchen im Konto */

export interface PaketAngebot {
  id: string;
  name: string;
  beschreibung: string;
  empfohlen: boolean;
  konditionen: Konditionen;
  /** im Paket enthaltene Add-ons */
  addons: import('./stripeAbrechnung').SperrbaresAddon[];
}

export interface MeinPaket {
  pakete: PaketAngebot[];
  /** gebuchtes Paket, null = noch keins */
  aktuell: { id: string; name: string; gebuchtAm: string | null } | null;
  /** vorgemerkter Wechsel zu einem günstigeren Paket */
  wechsel: { id: string; name: string; ab: string } | null;
  vertragBeginn: string | null;
  /** Ende der Testphase (inklusive), null = keine */
  testBis: string | null;
  /** individuelle Konditionen von DXL – überschreiben die des Pakets */
  individuell: boolean;
  /** Nutzer darf buchen (Administrator, nicht in der DXL-Ansicht) */
  darfBuchen: boolean;
  heute: string;
  /** Buchung und Zahlung über Stripe (Stripe von DXL eingerichtet) – sonst Buchung ohne Zahlung, Abrechnung per Rechnung */
  stripe: boolean;
  /** buchbare Add-ons */
  addons: import('./stripeAbrechnung').AddonAngebot[];
  /** gebuchte Add-ons */
  meineAddons: import('./stripeAbrechnung').MeinAddon[];
  /** Abo bei Stripe, null = keins */
  abo: import('./stripeAbrechnung').AboStand | null;
  /** wirksame Freischaltungen (Paket, Buchung, DXL) */
  freischaltung: import('./abrechnung').AddonStand;
}

export const PaketBuchenSchema = z.object({
  paketId: z.string().uuid(),
  zustimmung: z.literal(true, { errorMap: () => ({ message: 'Bitte bestätigen Sie die Konditionen.' }) }),
});

/** gebucht = erstes Paket; gewechselt = sofort (gleich teuer oder teurer); vorgemerkt = günstiger, gilt ab `ab` */
export interface PaketBuchenErgebnis {
  art: 'gebucht' | 'gewechselt' | 'vorgemerkt';
  paket: string;
  ab: string;
}

/** Art des Wechsels: Grundgebühr gleich oder höher → sofort, niedriger → zum nächsten Monatsersten */
export function paketWechselArt(alt: Konditionen | null, neu: Konditionen): 'sofort' | 'monatsende' {
  if (!alt) return 'sofort';
  return neu.grundgebuehr >= alt.grundgebuehr ? 'sofort' : 'monatsende';
}

